
import React, { useState, useEffect, useRef } from 'react';
import { Button, Stack, Typography, Box, Chip, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import dayjs from 'dayjs';
import '../styles/gridCalendar.scss';
import { useReservationStatusChange } from '../hooks/useReservationStatusChange';
import JsonDataModal from './editor2/modals/JsonDataModal';

// 車両 json_data の予約フィールド（モーダル上で削除不可）
const VEHICLE_JSON_RESERVED_FIELDS = ['label', 'mark', 'bg_color', 'text_color'];



// 車両json_dataのデフォルトカスタムフィールド
const VEHICLE_JSON_DATA_DEFAULTS = {
  data: {
    label: '',
    mark: '',
    bg_color: '',
    text_color: '',
  },
  types: {
    label: 'text',
    mark: 'text',
    bg_color: 'color',
    text_color: 'color',
  },
};

// 日本語表示ラベル
const JSON_DATA_FIELD_LABELS: Record<string, string> = {
  label: 'ラベル',
  mark: 'マーク',
  bg_color: '背景色',
  text_color: '文字色',
};

// 既存json_dataにデフォルトフィールドをマージ（既存フィールドは保持）
const initializeVehicleJsonData = (existing: any): any => {
  if (!existing || typeof existing !== 'object') {
    return JSON.parse(JSON.stringify(VEHICLE_JSON_DATA_DEFAULTS));
  }
  const mergedData = { ...VEHICLE_JSON_DATA_DEFAULTS.data, ...(existing.data || {}) };
  const mergedTypes = { ...VEHICLE_JSON_DATA_DEFAULTS.types, ...(existing.types || {}) };
  return { data: mergedData, types: mergedTypes };
};

// オブジェクトを比較してdirtyかどうかを判定
const isDirtyCheck = (original: any, current: any): boolean => {
  if (!original || !current) return false;
  // 比較するフィールド
  const fields = ['status', 'location_status', 'memo', 'cleaning', 'car_wash', 'check1', 'check2',
    'tire_groove', 'tire_pressure', 'reservation_box_priority', 'is_active', 'active_from', 'active_to', 'out_of_use'];

  // Check simple fields
  for (const field of fields) {
    if (original[field] !== current[field]) {
      return true;
    }
  }

  // check_situations comparison
  const originalChecks = original.check_situations || [];
  const currentChecks = current.check_situations || [];

  if (originalChecks.length !== currentChecks.length) return true;

  if (JSON.stringify(originalChecks) !== JSON.stringify(currentChecks)) return true;

  // json_data comparison
  if (JSON.stringify(original.json_data) !== JSON.stringify(current.json_data)) return true;

  // detail (所属/所有者/使用者 等) comparison
  if (JSON.stringify(original.detail) !== JSON.stringify(current.detail)) return true;

  return false;
};

interface VehicleDetailPanelProps {
  open: boolean;
  vehicle: any;
  onClose: () => void;
  onSave: (vehicle: any) => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
  vehicleStatusList?: any[];
  locationStatusList?: any[];
  onAssignClick?: () => void;
}

export default function VehicleDetailPanel({
  open,
  vehicle,
  onClose,
  onSave,
  onDirtyChange,
  vehicleStatusList,
  locationStatusList,
  onAssignClick,
}: VehicleDetailPanelProps) {
  const [editingVehicle, setEditingVehicle] = useState<any>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [jsonModalOpen, setJsonModalOpen] = useState<boolean>(false);
  const originalVehicleRef = useRef<any>(null);

  // propsが渡されない場合は空配列を使用
  const statusOptions: any[] = vehicleStatusList?.length ? vehicleStatusList : [];
  const locationOptions: any[] = locationStatusList?.length ? locationStatusList : [];



  useEffect(() => {
    if (vehicle) {
      const initVehicle = { ...vehicle };
      // is_activeが未定義の場合、activeプロパティ(ON/OFF)から初期値を設定
      if (initVehicle.is_active === undefined) {
        if (initVehicle.active !== undefined) {
          initVehicle.is_active = initVehicle.active === 'ON';
        } else {
          // default to true if neither exists
          initVehicle.is_active = true;
        }
      }
      // out_of_use default
      if (initVehicle.out_of_use === undefined) {
        initVehicle.out_of_use = false;
      }

      // json_data カスタムフィールド自動初期化
      initVehicle.json_data = initializeVehicleJsonData(initVehicle.json_data);

      setEditingVehicle(initVehicle);
      originalVehicleRef.current = initVehicle;
    }
  }, [vehicle]);

  // dirty状態を監視して親に通知
  useEffect(() => {
    if (onDirtyChange && originalVehicleRef.current) {
      const dirty = isDirtyCheck(originalVehicleRef.current, editingVehicle);
      onDirtyChange(dirty);
    }
  }, [editingVehicle, onDirtyChange]);

  const handleChange = (field: string, value: any) => {
    setEditingVehicle((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // --- 車両/移動ステータス即時保存フック ---
  const statusChange = useReservationStatusChange({
    onVehicleStatusSaved: (vehicleId, updates) => {
      // UIに即反映
      setEditingVehicle((prev: any) => ({ ...prev, ...updates }));
      originalVehicleRef.current = { ...originalVehicleRef.current, ...updates };
      if (onDirtyChange) onDirtyChange(isDirtyCheck(originalVehicleRef.current, { ...editingVehicle, ...updates }));
    },
    onToast: (message) => {
      console.log('[VehicleStatus]', message);
    },
  });

  const handleVehicleStatusClick = (newStatusId: number) => {
    if (String(safeVehicle.status) === String(newStatusId)) return;
    statusChange.requestVehicleStatusChange(
      String(safeVehicle.id),
      newStatusId,
      Number(safeVehicle.status),
    );
  };

  const handleLocationStatusClick = (newLocationStatusId: number | null) => {
    if (String(safeVehicle.location_status) === String(newLocationStatusId)) return;
    statusChange.requestLocationStatusChange(String(safeVehicle.id), newLocationStatusId);
  };


  const handleCheckboxChange = (field: string, checked: boolean) => {
    setEditingVehicle((prev: any) => ({
      ...prev,
      [field]: checked ? 1 : 0
    }));
  };

  const handleCheckSituationChange = (index: number, checked: boolean) => {
    setEditingVehicle((prev: any) => {
      const currentChecks = prev.check_situations ? [...prev.check_situations] : [];
      if (currentChecks[index]) {
        currentChecks[index] = { ...currentChecks[index], value: checked };
      }
      return { ...prev, check_situations: currentChecks };
    });
  };

  const handleJsonDataChange = (key: string, value: string) => {
    setEditingVehicle((prev: any) => ({
      ...prev,
      json_data: {
        ...prev.json_data,
        data: {
          ...(prev.json_data?.data || {}),
          [key]: value,
        },
      },
    }));
  };

  // 詳細情報（detail.affiliation / owner / user 等）の編集
  const handleDetailChange = (key: string, value: any) => {
    setEditingVehicle((prev: any) => ({
      ...prev,
      detail: {
        ...(prev.detail || {}),
        [key]: value,
      },
    }));
  };

  // JSONデータモーダルから保存
  const handleJsonModalSave = (newJsonData: any) => {
    setEditingVehicle((prev: any) => ({ ...prev, json_data: newJsonData }));
    setJsonModalOpen(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(editingVehicle);
      // 保存成功後、originalをリセット
      originalVehicleRef.current = { ...editingVehicle };
      if (onDirtyChange) onDirtyChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Safe object reference for multiple property accesses
  const safeVehicle: any = editingVehicle ?? {};

  // 文字列の"0"や数値の0をfalseとして扱うヘルパー
  const isChecked = (val: any): boolean => {
    if (val === '0' || val === 0 || val === 'false' || val === false || val === null || val === undefined || val === '') return false;
    return true;
  };

  return (
    <>
      <div className={`gc-sidepanel-backdrop ${open ? 'visible' : 'hidden'}`} onClick={onClose} aria-hidden="true" />
      <aside className={`gc-sidepanel ${open ? 'open' : 'closed'}`} role="dialog" aria-modal={open ? 'true' : 'false'} aria-hidden={open ? 'false' : 'true'}>
        <Box sx={{
          position: 'sticky',
          top: -12, // Compensate for parent padding
          mx: -1.5, // Compensate for parent padding (12px ~ 1.5 * 8px)
          px: 2,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #eee',
          marginBottom: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          zIndex: 10,
          backdropFilter: 'blur(4px)'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', m: 0 }}>車両詳細・編集</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="inherit" onClick={onClose} disabled={isSaving} size="small">
              閉じる
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving} size="small">
              {isSaving ? '保存中...' : '保存'}
            </Button>
          </Stack>
        </Box>
        <div className="gc-form" style={{ display: 'grid', gap: 16, padding: '0 4px 16px 4px', overflowY: 'auto' }}>

          <div className="gc-section-title" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: 4 }}>基本情報</div>

          <DetailItem label="車両名" value={safeVehicle.vehicle_type?.vehicle_type_name || safeVehicle.vehicle_code} readOnly />
          <div className="gc-field">
            <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>車両コード</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {onAssignClick && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={onAssignClick}
                  sx={{
                    fontSize: '0.75rem',
                    py: 0.5,
                    px: 1.5,
                    height: '34px',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#115293'
                    }
                  }}
                >
                  アサイン
                </Button>
              )}
              <div className="gc-input" style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '4px',
                padding: '8px 12px',
                minHeight: '38px',
                display: 'flex',
                alignItems: 'center',
                color: '#334155',
                flex: 1
              }}>
                {safeVehicle.vehicle_code}
              </div>
            </div>
          </div>
          <DetailItem label="ナンバープレート" value={safeVehicle.vehicle_number} readOnly />

          <div className="gc-field">
            <label className="gc-label">車両ステータス</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {statusOptions.map((opt: any) => {
                const isActive = String(safeVehicle.status) === String(opt.id);
                return (
                  <button
                    key={opt.id}
                    onClick={() => handleVehicleStatusClick(Number(opt.id))}
                    style={{
                      padding: '4px 10px',
                      fontSize: 12,
                      fontWeight: isActive ? 700 : 400,
                      border: isActive ? '2px solid #1e293b' : '1px solid #cbd5e1',
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: isActive ? (opt.bg_color || '#3b82f6') : (opt.bg_color ? `${opt.bg_color}33` : '#f1f5f9'),
                      color: isActive ? (opt.text_color || '#fff') : (opt.bg_color || '#334155'),
                      opacity: isActive ? 1 : 0.8,
                      transition: 'all 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="gc-field">
            <label className="gc-label">移動ステータス</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {locationOptions.map((opt: any) => {
                const isActive = String(safeVehicle.location_status) === String(opt.id);
                return (
                  <button
                    key={opt.id ?? 'null'}
                    onClick={() => handleLocationStatusClick(opt.id == null ? null : Number(opt.id))}
                    style={{
                      padding: '4px 10px',
                      fontSize: 12,
                      fontWeight: isActive ? 700 : 400,
                      border: isActive ? '2px solid #1e293b' : '1px solid #cbd5e1',
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: isActive ? (opt.bg_color || '#3b82f6') : (opt.bg_color ? `${opt.bg_color}33` : '#f1f5f9'),
                      color: isActive ? (opt.text_color || '#fff') : (opt.bg_color || '#334155'),
                      opacity: isActive ? 1 : 0.8,
                      transition: 'all 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="gc-field">
            <label className="gc-label">メモ</label>
            <textarea
              className="gc-input"
              value={safeVehicle.memo ?? ''}
              onChange={(e: any) => handleChange('memo', e.target.value)}
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="gc-section-title" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: 4, marginTop: 8 }}>整備・点検</div>



          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {Array.isArray(safeVehicle.check_situations) && safeVehicle.check_situations.length > 0 ? (
              safeVehicle.check_situations.map((item: any, index: number) => (
                <CheckboxItem
                  key={item.id || index}
                  label={item.name}
                  checked={isChecked(item.value)}
                  onChange={(c: boolean) => handleCheckSituationChange(index, c)}
                />
              ))
            ) : (
              <>
                <CheckboxItem label="車内清掃" checked={isChecked(safeVehicle.cleaning)} onChange={(c: boolean) => handleCheckboxChange('cleaning', c)} />
                <CheckboxItem label="洗車" checked={isChecked(safeVehicle.car_wash)} onChange={(c: boolean) => handleCheckboxChange('car_wash', c)} />
                <CheckboxItem label="日常点検" checked={isChecked(safeVehicle.check1)} onChange={(c: boolean) => handleCheckboxChange('check1', c)} />
                <CheckboxItem label="定期点検" checked={isChecked(safeVehicle.check2)} onChange={(c: boolean) => handleCheckboxChange('check2', c)} />
                <CheckboxItem label="タイヤ溝" checked={isChecked(safeVehicle.tire_groove)} onChange={(c: boolean) => handleCheckboxChange('tire_groove', c)} />
                <CheckboxItem label="空気圧" checked={isChecked(safeVehicle.tire_pressure)} onChange={(c: boolean) => handleCheckboxChange('tire_pressure', c)} />
              </>
            )}
          </div>

          <DetailItem
            label="最終確認日時"
            value={safeVehicle.last_checked_at ? dayjs(safeVehicle.last_checked_at).format('YYYY/MM/DD HH:mm') : '-'}
            readOnly
          />
          <DetailItem label="最終確認者ID" value={safeVehicle.last_checked_by ?? '-'} readOnly />

          <div className="gc-section-title" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: 4, marginTop: 8 }}>管理設定</div>

          <EditableDetailItem
            label="表示優先度"
            value={safeVehicle.reservation_box_priority}
            type="number"
            onChange={(val: any) => handleChange('reservation_box_priority', val)}
          />

          <div className="gc-field">
            <label className="gc-label">有効状態</label>
            <select
              className="gc-input"
              value={safeVehicle.is_active ? 'true' : 'false'}
              onChange={(e: any) => handleChange('is_active', e.target.value === 'true')}
            >
              <option value="true">ON（有効）</option>
              <option value="false">OFF（一時無効）</option>
            </select>
          </div>

          <div className="gc-field">
            <FormControlLabel
              control={
                <Switch
                  checked={safeVehicle.out_of_use || false}
                  onChange={(e: any) => {
                    const isOutOfUse = e.target.checked;
                    handleChange('out_of_use', isOutOfUse);
                    // 在庫(true)にする場合、有効状態(is_active)もOFF(false)にする
                    if (isOutOfUse) {
                      handleChange('is_active', false);
                    }
                  }}
                  color="error" // 赤色で強調（アーカイブ/停止の意味合い）
                />
              }
              label={
                <Typography style={{ fontWeight: safeVehicle.out_of_use ? 'bold' : 'normal', color: safeVehicle.out_of_use ? '#d32f2f' : 'inherit' }}>
                  {safeVehicle.out_of_use ? '在庫 (アーカイブ済み)' : '在庫 (アーカイブ) へ移動'}
                </Typography>
              }
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 1 }}>
              ※ONにすると有効状態がOFFになり、車両一覧の「アーカイブ」セクションに移動します
            </Typography>
          </div>

          <div className="gc-field">
            <label className="gc-label">有効期間</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="date"
                className="gc-input"
                value={safeVehicle.active_from ?? ''}
                onChange={(e: any) => handleChange('active_from', e.target.value)}
                style={{ flex: 1 }}
              />
              <span style={{ alignSelf: 'center' }}>〜</span>
              <input
                type="date"
                className="gc-input"
                value={safeVehicle.active_to ?? ''}
                onChange={(e: any) => handleChange('active_to', e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <div className="gc-section-title" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: 4, marginTop: 8 }}>カスタムフィールド</div>

          {safeVehicle.json_data?.data && Object.entries(safeVehicle.json_data.data).map(([key, value]: [string, any]) => {
            const fieldType = safeVehicle.json_data?.types?.[key] || 'text';
            const displayLabel = JSON_DATA_FIELD_LABELS[key] || key;

            if (fieldType === 'color') {
              return (
                <div className="gc-field" key={key}>
                  <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>{displayLabel}</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="color"
                      value={value || '#ffffff'}
                      onChange={(e: any) => handleJsonDataChange(key, e.target.value)}
                      style={{ width: 40, height: 34, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', padding: 2 }}
                    />
                    <input
                      type="text"
                      className="gc-input"
                      value={value ?? ''}
                      onChange={(e: any) => handleJsonDataChange(key, e.target.value)}
                      placeholder="#RRGGBB"
                      style={{
                        flex: 1,
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        minHeight: '38px',
                        color: '#334155'
                      }}
                    />
                    {value && (
                      <button
                        onClick={() => handleJsonDataChange(key, '')}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#94a3b8', padding: 4 }}
                        title="クリア"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <EditableDetailItem
                key={key}
                label={displayLabel}
                value={value ?? ''}
                onChange={(val: any) => handleJsonDataChange(key, val)}
              />
            );
          })}

          {/* JSONデータ（カスタムフィールド管理） */}
          <div className="gc-field" style={{ marginTop: 4 }}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={() => setJsonModalOpen(true)}
              sx={{
                borderRadius: 2,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: '#cbd5e1',
                color: '#475569',
              }}
            >
              その他データ（カスタムフィールドを追加・削除）
            </Button>
          </div>

          <div className="gc-section-title" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: 4, marginTop: 8 }}>詳細情報</div>

          <EditableDetailItem
            label="所属"
            value={safeVehicle.detail?.affiliation ?? ''}
            onChange={(val: any) => handleDetailChange('affiliation', val)}
          />
          <EditableDetailItem
            label="所有者"
            value={safeVehicle.detail?.owner ?? ''}
            onChange={(val: any) => handleDetailChange('owner', val)}
          />
          <EditableDetailItem
            label="使用者"
            value={safeVehicle.detail?.user ?? ''}
            onChange={(val: any) => handleDetailChange('user', val)}
          />

          <div className="gc-section-title" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: 4, marginTop: 8 }}>車両タイプ情報</div>

          <DetailItem label="車両タイプコード" value={safeVehicle.vehicle_type?.vehicle_type_code} readOnly />
          <DetailItem label="車両タイプ名" value={safeVehicle.vehicle_type?.vehicle_type_name} readOnly />
          <DetailItem label="カテゴリ" value={safeVehicle.vehicle_type?.vehicle_type_category} readOnly />
          <DetailItem label="定員" value={safeVehicle.vehicle_type?.vehicle_type_capacity} readOnly />

          {safeVehicle.vehicle_type?.bg_color && (
            <div className="gc-field">
              <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>表示カラー</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{
                  width: 24,
                  height: 24,
                  backgroundColor: safeVehicle.vehicle_type.bg_color,
                  border: '1px solid #e2e8f0',
                  borderRadius: 4
                }} />
                <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                  {safeVehicle.vehicle_type.bg_color}
                </span>
              </div>
            </div>
          )}

          <div className="gc-section-title" style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', paddingBottom: 4, marginTop: 8 }}>システム情報</div>

          <DetailItem label="車両ID" value={safeVehicle.id} readOnly />
          <DetailItem label="リソースID" value={safeVehicle.resource_id ?? '未割当'} readOnly />
          <DetailItem label="作成日時" value={safeVehicle.created_at ? dayjs(safeVehicle.created_at).format('YYYY/MM/DD HH:mm') : '-'} readOnly />
          <DetailItem label="更新日時" value={safeVehicle.updated_at ? dayjs(safeVehicle.updated_at).format('YYYY/MM/DD HH:mm') : '-'} readOnly />

        </div>
      </aside>

      {/* JSONデータモーダル（カスタムフィールドの追加・削除） */}
      <JsonDataModal
        open={jsonModalOpen}
        jsonData={safeVehicle.json_data}
        reservedFields={VEHICLE_JSON_RESERVED_FIELDS}
        title="JSONデータ設定"
        onSave={handleJsonModalSave}
        onCancel={() => setJsonModalOpen(false)}
      />

      {/* ステータス変更確認ダイアログ */}
      <Dialog
        open={statusChange.confirmDialog.open}
        onClose={() => statusChange.closeConfirmDialog(false)}
      >
        <DialogTitle>確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {statusChange.confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => statusChange.closeConfirmDialog(false)}>
            {statusChange.confirmDialog.noLabel || 'いいえ'}
          </Button>
          <Button onClick={() => statusChange.closeConfirmDialog(true)}>
            {statusChange.confirmDialog.yesLabel || 'はい'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface DetailItemProps {
  label: string;
  value: any;
  readOnly?: boolean;
}

function DetailItem({ label, value, readOnly }: DetailItemProps) {
  return (
    <div className="gc-field">
      <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>{label}</label>
      <div className="gc-input" style={{
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        padding: '8px 12px',
        minHeight: '38px',
        display: 'flex',
        alignItems: 'center',
        color: '#334155'
      }}>
        {value ?? '-'}
      </div>
    </div>
  );
}

interface EditableDetailItemProps {
  label: string;
  value: any;
  onChange: (val: any) => void;
  type?: string;
}

function EditableDetailItem({ label, value, onChange, type = 'text' }: EditableDetailItemProps) {
  return (
    <div className="gc-field">
      <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>{label}</label>
      <input
        type={type}
        className="gc-input"
        value={value ?? ''}
        onChange={(e: any) => onChange(e.target.value)}
        style={{
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '4px',
          padding: '8px 12px',
          minHeight: '38px',
          width: '100%',
          color: '#334155'
        }}
      />
    </div>
  );
}

interface CheckboxItemProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function CheckboxItem({ label, checked, onChange }: CheckboxItemProps) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: 4 }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e: any) => onChange(e.target.checked)}
        style={{ width: 18, height: 18 }}
      />
      <span style={{ fontSize: '0.9rem' }}>{label}</span>
    </label>
  );
}
